<script setup lang="ts">
import { ref } from 'vue';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { VForm } from 'vuetify/lib/components/index.mjs';
import Modal from '../Modal.vue';
import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import SelectGroupMemebers from '../../containers/SelectGroupMemebers.vue';
import { useManageGroup } from '../../composables/useManageGroup';
import { useAppEvents } from '../../events/appEvents';
import NewGroupForm from './NewGroupForm.vue';
import SuccessCard from './SuccessCard.vue';

const { t } = useLocale();
const { xs } = useDisplay();
const { organization } = useCurrentOrganization();
const { toast } = useToast();
const { emitAppEvent } = useAppEvents();
const { createGroup, addMembersToGroup, isCreatingGroup, isAddingMembers, addingMembersProgress } = useManageGroup();

type Props = {
  name: ModalTypes;
};
const props = defineProps<Props>();

const { isOpen, close } = useModal(props.name);

// step 1: Create group,
const groupForm = ref<VForm | null>(null);

//  step 2: Add members
const groupMembers = ref<string[]>([]);

// step 3: New group created

enum Step {
  CREATE_GROUP = 'createGroup',
  ADD_MEMBERS = 'addMembers',
  GROUP_CREATED = 'groupCreated',
}
const step = ref<Step>(Step.CREATE_GROUP);
const setStep = (newStep: Step) => {
  step.value = newStep;
};
const isStep = (checkStep: Step) => {
  return step.value === checkStep;
};

const onCreateGroup = async (): Promise<void> => {
  if (!groupForm.value) {
    console.error('Group Form is not found');
    return;
  }
  if (!groupForm.value.isValid) {
    console.error('Group form is not valid');
    return;
  }

  if (!organization.value?.uid) {
    console.error('Organization is not selected');
    return;
  }

  const newGroup = await createGroup(
    organization.value?.uid,
    groupForm.value?.name,
    groupForm.value?.type,
    groupForm.value?.yearsInSchool,
    false,
    groupForm.value?.expiresAt ?? null,
  ).catch((err: any) => {
    if (err?.response?.status === 409) {
      // name is already in use, go back to create group step
      setStep(Step.CREATE_GROUP);

      groupForm.value?.resetValidationAndValidate();
      toast(t('lcp.group_name_taken'), 'error');
    } else {
      toast(t('lcp.failed_to_create_group'), 'error');
    }
  });

  if (!newGroup) {
    console.error('Failed to create group');
    return;
  }

  // add memebers to group
  if (groupMembers.value.length > 0) {
    const usersToPrecess = await addMembersToGroup(newGroup.uid, groupMembers.value);

    emitAppEvent({ type: 'membership:updated', organizationUid: organization.value!.uid });

    if (usersToPrecess.filter((m) => m.status === 'failed').length === 0) {
      toast(t('lcp.group_created_successfully_count_members', { count: groupMembers.value.length }), 'success');
    } else {
      toast(
        t('lcp.group_updated_successfully_count_members_failed', {
          count: usersToPrecess.filter((m) => m.status === 'successful').length,
          failedCount: usersToPrecess.filter((m) => m.status === 'failed').length,
        }),
        'warning',
      );
    }
  } else {
    toast(t('lcp.group_created_successfully'), 'success');
  }

  setStep(Step.GROUP_CREATED);

  groupMembers.value = [];
  groupForm.value?.reset();
};

const onReset = () => {
  groupForm.value?.reset();
  groupMembers.value = [];
  setStep(Step.CREATE_GROUP);
};

const onClose = () => {
  close();
  onReset();
};

const modalTitle = computed(() => {
  if (isStep(Step.ADD_MEMBERS)) {
    return t('lcp.add_members');
  }

  if (isStep(Step.GROUP_CREATED)) {
    return t('lcp.new_group_created');
  }

  return t('lcp.create_new_group');
});
const actionLabel = computed(() => {
  if (isStep(Step.CREATE_GROUP)) {
    return t('lcp.add_members');
  }
  if (isStep(Step.ADD_MEMBERS)) {
    return t('lcp.create_group_with_members_count', { members_count: groupMembers.value.length });
  }
  return t('lcp.close');
});

const action = computed(() => {
  if (isStep(Step.CREATE_GROUP)) {
    return () => setStep(Step.ADD_MEMBERS);
  }
  if (isStep(Step.ADD_MEMBERS)) {
    return onCreateGroup;
  }

  return onClose;
});

const topLeftBtnAction = computed(() => {
  if (isStep(Step.ADD_MEMBERS)) {
    return () => setStep(Step.CREATE_GROUP);
  }
  if (isStep(Step.GROUP_CREATED) && !xs.value) {
    return undefined;
  }

  return onClose;
});
const topLeftBtnLabel = computed(() => {
  return isStep(Step.ADD_MEMBERS) ? t('lcp.back') : t('lcp.close');
});
const topLeftBtnIcon = computed(() => {
  return isStep(Step.ADD_MEMBERS) ? 'mdi-chevron-left' : 'mdi-close';
});

const isGroupFormValid = computed(() => {
  if (!groupForm.value) return false;

  return groupForm.value.isValid;
});
</script>

<template>
  <Modal
    :name="name"
    :title="modalTitle"
    :is-open="isOpen"
    :show-cancel-action="false"
    fullscreen
    contained
    background-color="surface-container"
    :top-left-btn-action="topLeftBtnAction"
    :top-left-btn-label="topLeftBtnLabel"
    :top-left-btn-icon="topLeftBtnIcon"
    :show-close-btn="false"
    :loading="isCreatingGroup || isAddingMembers"
    @close="onClose"
  >
    <v-scroll-x-transition hide-on-leave>
      <v-sheet
        v-show="step === Step.CREATE_GROUP"
        max-width="740"
        rounded
        class="mx-auto bg-surface-bright pa-10"
        :class="{ 'bg-surface-container': xs, 'pa-10': !xs, 'px-0': xs, 'pb-0': xs }"
      >
        <NewGroupForm ref="groupForm" />
      </v-sheet>
    </v-scroll-x-transition>
    <v-scroll-x-reverse-transition hide-on-leave>
      <v-sheet v-show="step === Step.ADD_MEMBERS" max-width="740" rounded class="mx-auto bg-surface-container">
        <SelectGroupMemebers ref="membersRef" v-model="groupMembers" :organization-uid="organization?.uid" />
        <v-progress-linear
          v-if="groupMembers.length > 0 && isAddingMembers"
          v-model="addingMembersProgress"
          color="primary"
          height="4"
          rounded
          class="mt-4"
        ></v-progress-linear>
      </v-sheet>
    </v-scroll-x-reverse-transition>
    <v-scroll-x-transition hide-on-leave>
      <SuccessCard
        v-show="step === Step.GROUP_CREATED"
        :on-reset="onReset"
        :action="action"
        :actionLabel="actionLabel"
      />
    </v-scroll-x-transition>

    <v-sheet
      v-if="step !== Step.GROUP_CREATED"
      max-width="740"
      rounded
      class="mx-auto bg-surface-container py-6 d-flex justify-end"
    >
      <v-btn
        id="action-btn"
        :block="xs"
        @click="action"
        append-icon="mdi-chevron-right"
        :disabled="!isGroupFormValid || isCreatingGroup || isAddingMembers"
      >
        {{ actionLabel }}
      </v-btn>
    </v-sheet>
  </Modal>
</template>

<style scoped></style>
